import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { debounceTime, Observable, Subscription } from 'rxjs';
import { Site } from 'src/app/model/site';
import { AppState } from 'src/app/store/app.reducer';
import * as NotificationsActions from "src/app/store/notifications/notifications.actions";
import * as UserActions from 'src/app/store/user/user.actions';
import * as UserFileActions from 'src/app/store/user-file/user-file.actions';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';
import { selectCurrentLang, selectIsImpersMode, selectUser } from 'src/app/store/user/user.selector';
import { selectSites, selectSitesEmpty, selectSitesLoading } from 'src/app/store/site/site.selector';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { SiteDialogComponent } from '../site-dialog/site.dialog.component';
import * as SiteActions from 'src/app/store/site/site.actions';
import { StandardDesignParameters, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { User } from 'src/app/model/user';
import { DEFAULT_LANG, LANGUAGES } from 'src/app/conf/languages';
import { Lang } from 'src/app/model/lang';
import { InvitationData } from 'src/app/model/api/invitation-data';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { ActivatedRoute } from '@angular/router';
import { AppComponent } from 'src/app/app.component';
import { FormControl, FormGroup } from '@angular/forms';
import { selectVctSitesList } from 'src/app/store/device-vct/device-vct.selector';

@Component({
	selector: 'app-sites',
	templateUrl: './sites.component.html',
	providers: [ConfirmationService, DialogService]
})

export class SitesComponent implements OnInit, OnDestroy {
	loading$: Observable<boolean | undefined>;
	loadingSub: Subscription | undefined;
	sites$: Observable<Site[] | undefined>;
	sitesSub: Subscription | undefined;
	sitesEmpty$: Observable<boolean | undefined>;
	sitesEmptySub: Subscription | undefined;
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;
	sitesVct$: Observable<Site[] | undefined>;
	sitesVctSub: Subscription | undefined;

	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;
	languages = LANGUAGES;

	currentUser: User;
	currentUser$: Observable<User | undefined>;
	currentUserSub: Subscription | undefined;

	sites: Site[];
	sitesVct: Site[];
	sitesAll: Site[];
	sitesOrder: Site[];

	loading: boolean;
	sitesEmpty: boolean;
	isImpersMode: boolean;
	isDraftMode: boolean;

	siteFilter: FormGroup;
	filterSub: Subscription;
	// siteType: any[];

	analytics = this.app.initFirebase();

	ref: DynamicDialogRef | undefined;

	screenWidth: number;
	MAX_MD_WIDTH = StandardDesignParameters.MAX_MD_WIDTH;
	LG_WIDTH = StandardDesignParameters.LG_WIDTH;

	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private route: ActivatedRoute,
		private translate: TranslateService,
		public dialogService: DialogService,
		public app: AppComponent
	) {
		this.initSelectors();
	}

	ngOnInit() {
		this.store.dispatch(NotificationsActions.setTopbarTitleId({
			stringId: 'SITES_MANAGEMENT'
		}));
		this.siteFilter = new FormGroup({
			'searchFilter': new FormControl(''),
			'ownerSites': new FormControl(true),
			'sharedSites': new FormControl(true),
			'vctSites': new FormControl(true),
			// 'type': new FormControl(this.siteType[0].value),
		});
		this.store.dispatch(UserActions.getTechnicians());
		this.store.dispatch(UserFileActions.getSiteImages());
		this.subscribeAll();
		this.invitationData();
		this.onResize();
		setTimeout(() => {
			this.store.dispatch(AnalyticsActions.eventCustomPageView({ params: { page_title: 'Sites' } }));
		}, 2000);

		this.filterSub = this.siteFilter.valueChanges.pipe(
			debounceTime(500),
		).subscribe(() => {
			this.filterSite();
		});
	}

	filterSite(): void {
		const nameFilter = this.f.searchFilter.value;
		const ownerChecked = this.f.ownerSites.value;
		const sharedChecked = this.f.sharedSites.value;
		const vctChecked = this.f.vctSites.value;
		// const typeFilter = this.f.type.value;
		this.sitesOrder = [];
		for (const site of this.sitesAll) {
			const matchesName: boolean = nameFilter === undefined || nameFilter === '' || site.Name.toLowerCase().includes(nameFilter.toLowerCase());

			// No checkbox selected → skip everything
			if (!ownerChecked && !sharedChecked && !vctChecked) {
				continue;
			}

			let matchesType: boolean = false;
			if ((ownerChecked && site.CurrentlyOwned) || (sharedChecked && !site.CurrentlyOwned && !site.isOnlySiteVct) || (vctChecked && site.isOnlySiteVct)) {
				matchesType = true;
			}

			if (matchesName && matchesType) {
				this.sitesOrder.push(site);
			}
		}
		this.sitesOrder = this.orderSites(this.sitesOrder);
	}

	invitationData(): void {
		const invitationData = this.route.snapshot.queryParams['invitationData'];
		if (invitationData) {
			const decodedData: InvitationData = JSON.parse(atob(invitationData));
			if (decodedData.SiteId) {
				this.store.dispatch(SiteActions.confirmSiteTransfer({
					siteId: decodedData.SiteId,
					ownerId: decodedData.OwnerId,
					verification: decodedData.Verification
				}));
			} else if (decodedData.DeviceId) {
				this.store.dispatch(DeviceActions.confirmShareDevice({ invitationData: decodedData }));
			} else {
				this.store.dispatch(SiteActions.confirmSiteDeviceTransferShareError({
					errorMessage: this.translate.instant('ERROR_CONFIRM_SHARE_DEVICE')
				}));
			}
		}
	}

	initSelectors(): void {
		// Store selectors
		this.loading$ = this.store.select(selectSitesLoading);
		this.sites$ = this.store.select(selectSites);
		this.sitesEmpty$ = this.store.select(selectSitesEmpty);
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.currentUser$ = this.store.select(selectUser);
		this.sitesVct$ = this.store.select(selectVctSitesList);
	}

	subscribeAll(): void {
		this.loadingSub = this.loading$?.subscribe(
			(loading) => {
				this.loading = loading;
			}
		);
		this.sitesSub = this.sites$?.subscribe(
			(sites) => {
				this.sites = sites;
				this.mergeSite();
			}
		);
		this.sitesEmptySub = this.sitesEmpty$?.subscribe(
			(sitesEmpty) => {
				this.sitesEmpty = sitesEmpty;
			}
		);
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
			}
		);
		this.currentLang$?.subscribe((lang: Lang) => {
			this.currentLang = lang;
		});
		this.currentUserSub = this.currentUser$?.subscribe(currentUser => {
			this.currentUser = currentUser;
		});
		this.sitesVctSub = this.sitesVct$?.subscribe(
			(sitesVct) => {
				this.sitesVct = sitesVct;
				this.mergeSite();
			}
		);
	}


	mergeSite(): void {
		this.sitesAll = [];
		if (this.sites !== undefined && this.sites !== null) {
			this.sitesAll = [...this.sites];
			if (this.sitesVct !== undefined && this.sitesVct !== null) {
				this.mergeSiteList();
			}
		} else if (this.sitesVct !== undefined && this.sitesVct !== null) {
			this.sitesAll = [...this.sitesVct];
		}
		this.sitesOrder = this.orderSites(this.sitesAll);
	}

	mergeSiteList(): void {
		for (const site of this.sitesVct) {
			const find = this.sitesAll.find(s => s.Id === site.Id);
			if (find === undefined) {
				const siteAdd = Object.assign({}, site);
				siteAdd.isOnlySiteVct = true;
				this.sitesAll.push(siteAdd);
			}
		}
	}

	orderSites(sites: Site[]): Site[] {
		if (sites !== undefined && sites.length > 0) {
			sites = sites.sort((a, b) => { return a.Name < b.Name ? -1 : 1 });
		}
		return sites;
	}

	ngOnDestroy(): void {
		this.loadingSub?.unsubscribe();
		this.sitesSub?.unsubscribe();
		this.sitesEmptySub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
		this.currentUserSub?.unsubscribe();
		this.sitesVctSub?.unsubscribe();
	}

	createSite(): void {
		this.store.dispatch(SiteActions.resetAddEditSiteForm());
		this.ref = this.dialogService.open(SiteDialogComponent, {
			header: this.translate.instant('ADD_SITE'),
			width: '85vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
		}
		);
	}

	get f(): any {
		return this.siteFilter.controls;
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	onTablet(): boolean {
		return StandardPageLayout.onTablet(this.screenWidth);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	col4Responsive(): string {
		return StandardPageLayout.col4ResponsiveStd(this.screenWidth);
	}

	colFilterResponsive(elemType: string): string {
		return StandardPageLayout.colFilterResponsive(this.screenWidth, elemType);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-23rem');
	}

	tabletUnderLgWidth(): boolean {
		return StandardPageLayout.tabletUnderLgWidth(this.screenWidth);
	}
}
